import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import type { MeasurementDataAPIRequest, MeasurementDataByGroupRequest, MeasurementDataRequest, TelemetryDataModel } from 'commons';
import { catchError, map, of } from 'rxjs';
import type { TestStateModel, TestStateRequestBody } from './models/TestStateModel';
import { ServiceUtils } from './utils';

@Injectable({
  providedIn: 'root',
})
export class TelemetryApiService {
  private readonly snack = inject(MatSnackBar);
  private readonly http = inject(HttpClient);
  /**
   *
   */
  private readonly BASE = '/api/telemetry/v2';
  /**
   *
   * @param channelId
   * @param options
   * @returns
   */
  getChannelsData(options: MeasurementDataRequest) {
    const opt: MeasurementDataAPIRequest = {
      ...options,
      startTime: options.startTime ? ServiceUtils.toTime(options.startTime) : undefined,
      endTime: options.endTime ? ServiceUtils.toTime(options.endTime) : undefined,
    };
    return this.http.post<TelemetryDataModel>(`${this.BASE}/twins/groups/channels/data`, opt).pipe(
      map((data) => {
        if (!data || data?.channelValues.length <= 0) {
          this.snack.open(
            `No data found for channel between ${options.startTime} and ${options.endTime} at ${options.windowSize} ${options.windowUnit} Resolution`,
            'close',
            {
              duration: 10000,
            }
          );
          throw new Error('no data found');
        }
        return data;
      })
    );
  }
  /**
   *
   * @param options
   * @returns
   */
  getGroupData(groupID: string, options: MeasurementDataByGroupRequest) {
    const opt: MeasurementDataAPIRequest = {
      ...options,
      startTime: ServiceUtils.toTime(options.startTime),
      endTime: ServiceUtils.toTime(options.endTime),
    };

    return this.http.post<TelemetryDataModel>(`${this.BASE}/twins/groups/${groupID}/data`, opt).pipe(
      catchError(() => {
        return of({} as TelemetryDataModel);
      })
    );
  }

  /**
   * Fetches test states for a given group/configuration.
   *
   * @param groupId - The group (configuration) identifier
   * @returns Observable of test state array
   */
  getTestStates(groupId: string) {
    return this.http.get<TestStateModel[]>(`${this.BASE}/twins/groups/${groupId}/teststates`).pipe(
      catchError((error) => {
        console.error('Error fetching test states:', error);
        this.snack.open('Failed to fetch test states', 'close', {
          duration: 5000,
        });
        return of([] as TestStateModel[]);
      })
    );
  }

  /**
   * Creates a new test state for a given group/configuration.
   *
   * POST /api/telemetry/v2/twins/groups/{groupId}/teststates
   * @param groupId - The group (configuration) identifier
   * @param body - The test state data to create
   * @returns Observable of the created test state
   */
  createTestState(groupId: string, body: TestStateRequestBody) {
    return this.http.post<TestStateModel>(`${this.BASE}/twins/groups/${groupId}/teststates`, body).pipe(
      catchError((error) => {
        console.error('Error creating test state:', error);
        this.snack.open('Failed to create test state', 'close', {
          duration: 5000,
        });
        throw error;
      })
    );
  }

  /**
   * Updates an existing test state.
   *
   * PATCH /api/telemetry/v2/twins/groups/teststates/{testStateId}
   * @param testStateId - The test state identifier
   * @param body - The test state data to update
   * @returns Observable of the updated test state
   */
  updateTestState(testStateId: string, body: TestStateRequestBody) {
    return this.http.patch<TestStateModel>(`${this.BASE}/twins/groups/teststates/${testStateId}`, body).pipe(
      catchError((error) => {
        console.error('Error updating test state:', error);
        this.snack.open('Failed to update test state', 'close', {
          duration: 5000,
        });
        throw error;
      })
    );
  }

  /**
   * Deletes a test state.
   *
   * DELETE /api/telemetry/v2/twins/groups/teststates/{testStateId}
   * @param testStateId - The test state identifier
   */
  deleteTestState(testStateId: string) {
    return this.http.delete<void>(`${this.BASE}/twins/groups/teststates/${testStateId}`).pipe(
      catchError((error) => {
        console.error('Error deleting test state:', error);
        this.snack.open('Failed to delete test state', 'close', {
          duration: 5000,
        });
        throw error;
      })
    );
  }

  /**
   * Approves a test state.
   *
   * PATCH /api/telemetry/v2/twins/teststates/{testStateId}/approval
   * @param testStateId - The test state identifier
   * @returns Observable of the approved test state
   */
  approveTestState(testStateId: string) {
    return this.http
      .patch<TestStateModel>(
        // `${this.BASE}/twins/teststates/${testStateId}/approval`,
        `${this.BASE}/twins/groups/teststates/${testStateId}/approval`,
        {}
      )
      .pipe(
        catchError((error) => {
          console.error('Error approving test state:', error);
          this.snack.open('Failed to approve test state', 'close', {
            duration: 5000,
          });
          throw error;
        })
      );
  }
}
